import {
  LayoutDashboard,
  CheckSquare,
  Users,
  UsersRound,
  ShoppingCart,
  BarChart3,
  FolderOpen,
  FileSignature,
  Settings,
  Headphones,
  Ticket,
  Copy,
  ClipboardList,
  FileText,
  Library,
  MoreHorizontal,
  UserCog,
  Receipt,
  Upload,
  Lightbulb,
  HelpCircle,
  Globe2,
  Megaphone,
  GraduationCap,
  LineChart,
  CircleDollarSign,
  CalendarClock,
  Target,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { UserType } from '@/types'

export interface NavItem {
  label: string
  href: string
  icon?: LucideIcon
  allowedUserTypes?: UserType[]
}

export interface NavGroup {
  id: string
  label: string
  icon: LucideIcon
  href?: string
  items?: NavItem[]
  defaultExpanded?: boolean
  standalone?: boolean
  allowedUserTypes?: UserType[]
  /** Exclude from the sidebar/mobile drawer while remaining available to the
   *  command palette + favorites (e.g. Admin lives in the user-avatar menu). */
  hideFromSidebar?: boolean
}

export const navigationGroups: NavGroup[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
    standalone: true,
    // Visible to all CRM user types — but NOT HR-only 'Employee' users.
    allowedUserTypes: [
      'Super Admin',
      'Admin',
      'User',
      'Marketing',
      'Orders',
      'Quotes',
      'Support',
      'RFP',
    ],
  },
  {
    id: 'support',
    label: 'Support',
    icon: Headphones,
    defaultExpanded: false,
    allowedUserTypes: [
      'Super Admin',
      'Admin',
      'User',
      'Support',
      'Marketing',
      'Orders',
      'Quotes',
      'RFP',
    ],
    items: [
      { label: 'Tickets', href: '/tickets', icon: Ticket },
      { label: 'Internal Support', href: '/support-request', icon: ClipboardList },
    ],
  },
  {
    id: 'activities',
    label: 'Activities',
    icon: CheckSquare,
    defaultExpanded: true,
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes', 'RFP'],
    items: [
      { label: 'Tasks', href: '/tasks' },
      { label: 'Events', href: '/events' },
      {
        label: 'Emails',
        href: '/emails',
        allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes'],
      },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: Users,
    defaultExpanded: true,
    allowedUserTypes: [
      'Super Admin',
      'Admin',
      'User',
      'Marketing',
      'Orders',
      'Quotes',
      'RFP',
      'Support',
    ],
    items: [
      { label: 'Accounts', href: '/accounts' },
      { label: 'Contacts', href: '/contacts' },
      {
        label: 'Leads',
        href: '/leads',
        allowedUserTypes: [
          'Super Admin',
          'Admin',
          'User',
          'Marketing',
          'Orders',
          'Quotes',
          'Support',
        ],
      },
      {
        label: 'Opportunities',
        href: '/opportunities',
        allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Quotes', 'Orders', 'Support'],
      },
      {
        label: 'Duplicates',
        href: '/duplicates',
        icon: Copy,
        allowedUserTypes: ['Super Admin', 'Admin'],
      },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: ShoppingCart,
    defaultExpanded: false,
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Orders', 'Quotes', 'RFP'],
    items: [
      {
        label: 'Quotes',
        href: '/quotes',
        allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Quotes', 'Orders'],
      },
      {
        label: 'Orders',
        href: '/orders',
        allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Orders'],
      },
      {
        label: 'Products',
        href: '/products',
        allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Orders', 'Quotes', 'RFP'],
      },
      { label: 'Price List', href: '/pricebooks', allowedUserTypes: ['Super Admin', 'Admin'] },
      // Contract reviews. Anyone commercial can raise one; the LIST itself is filtered to the
      // requester, the reviewer and admins in the route layer, so a user with none sees an
      // empty page rather than other people's contract terms.
      {
        label: 'Contract reviews',
        href: '/contract-reviews',
        icon: FileSignature,
        // BDR is absent on purpose: routes/accounts.js does not admit BDR, so the customer
        // picker on the raise form 403s for them. See contractReviews.js.
        allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Orders', 'Quotes', 'RFP'],
      },
      {
        label: 'Battlecards',
        href: '/battlecards',
        allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Orders'],
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    defaultExpanded: false,
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes', 'RFP'],
    items: [
      {
        label: 'Analytics',
        href: '/analytics',
        allowedUserTypes: ['Super Admin', 'Admin', 'Marketing'],
      },
      {
        label: 'Lead Sources',
        href: '/analytics/lead-sources',
        allowedUserTypes: ['Super Admin', 'Admin', 'Marketing'],
      },
      { label: 'Reports', href: '/reports' },
      {
        label: 'Marketing Reports',
        href: '/reports/marketing',
        icon: Megaphone,
        allowedUserTypes: ['Super Admin', 'Admin', 'Marketing'],
      },
      { label: 'Where in the World', href: '/where-in-the-world', icon: Globe2 },
    ],
  },
  {
    // QuickBooks billings actuals. Admin-only for now: this is company-wide
    // revenue and a rep league table.
    //
    // Reps will eventually see their own sheet, and when they do it must be a new
    // item pointed at a session-scoped endpoint - NOT this band with a widened
    // allowedUserTypes. Every read behind these pages takes the rep from the URL,
    // so widening the guard would let one rep read another's commission.
    id: 'sales-trackers',
    label: 'Sales Trackers',
    icon: LineChart,
    defaultExpanded: false,
    // Both types must be listed: nav filtering has NO admin bypass, unlike
    // ProtectedRoute and restrictToTypes.
    // The BAND now admits reps so "My sales" can appear. Every admin-only item inside
    // carries its own allowedUserTypes, so a rep sees exactly one entry here. Both the
    // band and each item must list a type: nav filtering has no admin bypass.
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'BDR'],
    items: [
      // Reps see ONLY this one. Every other item in this band is admin-only, and the
      // band itself lists the rep types so it appears for them at all - nav filtering
      // has no admin bypass, unlike ProtectedRoute.
      {
        label: 'My sales',
        href: '/sales-trackers/my-sales',
        allowedUserTypes: ['User', 'BDR', 'Admin', 'Super Admin'],
      },
      { label: 'Overview', href: '/sales-trackers', allowedUserTypes: ['Admin', 'Super Admin'] },
      // Where the Monday sales meeting is run from. Sits directly under Overview
      // because it is the weekly rhythm, not a drill-down.
      {
        label: 'Weekly forecast',
        href: '/sales-trackers/forecast',
        icon: CalendarClock,
        allowedUserTypes: ['Admin', 'Super Admin'],
      },
      {
        label: 'By Product',
        href: '/sales-trackers/products',
        icon: CircleDollarSign,
        allowedUserTypes: ['Admin', 'Super Admin'],
      },
      {
        label: 'By Rep',
        href: '/sales-trackers/reps',
        icon: UsersRound,
        allowedUserTypes: ['Admin', 'Super Admin'],
      },
      // Labelled for what it is now: the decision queue plus the product and rep
      // management Jason asked for. Only the NAV label changes - the page keeps its
      // "Billings attribution" <h1>, because wcag-compliance.spec.ts gates that page
      // on `text=Attribution` and the route keeps its path.
      // The individual invoice lines behind every figure above. Two entries, not one with
      // a widened guard: the admin route takes a rep from the URL, the rep route takes the
      // rep from the session and has no identifier to tamper with.
      {
        label: 'Transactions',
        href: '/sales-trackers/transactions',
        icon: Receipt,
        allowedUserTypes: ['Admin', 'Super Admin'],
      },
      {
        label: 'My transactions',
        href: '/sales-trackers/my-transactions',
        icon: Receipt,
        // Admins are listed for the same reason "My sales" lists them: nav filtering has
        // NO admin bypass, unlike ProtectedRoute, so an admin using View As on a rep could
        // reach the route by typing the URL but had no link to it. The two rep pages were
        // reachable by different means, which is the kind of difference nobody discovers
        // until they are trying to check what a rep can see.
        allowedUserTypes: ['User', 'BDR', 'Admin', 'Super Admin'],
      },
      // Set the whole year for everybody in one place. Sits next to Products & reps
      // because both are configuration rather than reporting.
      {
        label: 'Targets',
        href: '/sales-trackers/targets',
        icon: Target,
        allowedUserTypes: ['Admin', 'Super Admin'],
      },
      {
        label: 'Products & reps',
        href: '/sales-trackers/attribution',
        icon: UserCog,
        allowedUserTypes: ['Admin', 'Super Admin'],
      },
      // Uploading is NOT a band item. It happens once a month, from Overview, which
      // is where the "Upload QuickBooks export" button and the last-import line live.
      // The route /sales-trackers/upload still exists and still works; it is simply
      // not something to walk past four times a day.
    ],
  },
  {
    id: 'rfps',
    label: 'RFPs',
    icon: FileText,
    href: '/rfps',
    standalone: true,
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'RFP', 'Orders'],
  },
  {
    id: 'content',
    label: 'Content',
    icon: FolderOpen,
    href: '/content',
    standalone: true,
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes'],
  },
  {
    // Low-frequency destinations consolidated to keep the main nav short.
    // Items keep their own role gating; the group is visible whenever it has
    // at least one visible item after filtering.
    id: 'more',
    label: 'More',
    // Kept in step with the /repository route in App.tsx, which admits exactly these.
    // Ungated, this group showed Repository to Training, Employee and BDR accounts, all
    // three of which the route then bounced — a link to a door that will not open.
    // Nav filtering has no admin bypass (see getVisibleNavGroups), so the two admin
    // types are listed explicitly.
    allowedUserTypes: [
      'Super Admin',
      'Admin',
      'User',
      'Marketing',
      'Orders',
      'Quotes',
      'Support',
      'RFP',
    ],
    icon: MoreHorizontal,
    defaultExpanded: false,
    items: [{ label: 'Repository', href: '/repository', icon: Library }],
  },
  {
    // HR module. Items keep their own role gating; the group is visible whenever
    // it has at least one visible item (e.g. Time Off shows for everyone).
    id: 'hr',
    label: 'HR',
    icon: UsersRound,
    defaultExpanded: false,
    // Staff-facing HR items are visible to everyone (the backend scopes the data
    // to self + your team). Management items keep an admin gate in the UI and are
    // additionally HR-admin-enforced server-side.
    items: [
      { label: 'HR Home', href: '/hr', icon: UsersRound },
      { label: 'My Profile', href: '/hr/me' },
      { label: 'Directory', href: '/hr/employees' },
      { label: 'Org Chart', href: '/hr/org-chart' },
      {
        label: 'Where in the World',
        href: '/hr/where-in-the-world',
        icon: Globe2,
        allowedUserTypes: ['Super Admin'],
      },
      { label: 'Time Off', href: '/hr/time-off' },
      { label: 'Leave Approvals', href: '/hr/leave-approvals' },
      {
        label: 'Departments',
        href: '/hr/departments',
        allowedUserTypes: ['Super Admin', 'Admin'],
      },
      {
        label: 'Company Documents',
        href: '/hr/company-documents',
        allowedUserTypes: ['Super Admin', 'Admin'],
      },
    ],
  },
  {
    // Lives in the user-avatar dropdown rather than the sidebar (still reachable
    // via the command palette + favorites).
    id: 'admin',
    label: 'Admin',
    icon: Settings,
    defaultExpanded: false,
    hideFromSidebar: true,
    items: [
      { label: 'Settings', href: '/settings', icon: Settings },
      { label: 'Users', href: '/users', icon: UserCog, allowedUserTypes: ['Super Admin', 'Admin'] },
      {
        label: 'Mass Upload',
        href: '/admin/mass-upload',
        icon: Upload,
        allowedUserTypes: ['Super Admin', 'Admin'],
      },
      {
        label: 'Enhancement Tracker',
        href: '/enhancement-tracker',
        icon: Lightbulb,
        allowedUserTypes: ['Super Admin', 'Admin'],
      },
      { label: 'Help', href: '/help', icon: HelpCircle },
    ],
  },
  {
    // Last in the sidebar, by request. A Training account sees only this group, so
    // its position makes no difference to them; for everyone else it is the section
    // they visit least, and it was previously sitting above Dashboard.
    //
    // Visible to every CRM type AND to Training accounts, whose only reason for
    // having a login is these lists. Delivery is cross-functional — a rep wants to
    // know whether the training they sold has happened.
    //
    // One entry per tab of the "Training Tracking" workbook, so the CRM and the
    // spreadsheet can be read side by side while the two overlap.
    id: 'training',
    label: 'Training (Beta)',
    icon: GraduationCap,
    defaultExpanded: false,
    items: [
      { label: 'Worklist', href: '/training' },
      { label: 'PDF Onboarding', href: '/training/onboarding' },
      { label: 'Online Beta', href: '/training/beta' },
      { label: 'Advanced Demo', href: '/training/demos' },
      { label: 'Webinars', href: '/training/webinars' },
      { label: 'Office Hours', href: '/training/office-hours' },
      {
        // Admin-only, unlike the rest of the group: this writes hundreds of records,
        // and on prod it is the only way this data arrives at all.
        label: 'Import tracker',
        href: '/training/import',
        allowedUserTypes: ['Super Admin', 'Admin'],
      },
    ],
    allowedUserTypes: [
      'Super Admin',
      'Admin',
      'User',
      'Marketing',
      'Orders',
      'Quotes',
      'Support',
      'RFP',
      'Training',
    ],
  },
]

/** A single navigable destination, flattened from groups + standalone items. */
export interface NavDestination {
  label: string
  href: string
  icon: LucideIcon
  /** The owning group's label (e.g. "CRM"); equals label for standalone items. */
  groupLabel: string
}

/**
 * Navigation groups visible to a user type, with role-gated items removed and
 * any now-empty groups dropped. Single source of truth for the sidebar, mobile
 * drawer, favorites, recents, and the command palette.
 */
/**
 * User types confined to a single workspace, who must see ONLY what names them.
 *
 * A group with no `allowedUserTypes` means "everyone", which is the right default for
 * staff and the wrong one for a contained account: a Training login exists solely to
 * work the training lists, and the server confines it to /api/v1/training regardless of
 * what the sidebar offers (see middleware/auth.js). The nav was not matching that — an
 * ungated group showed up for them, and the route behind it then refused them.
 *
 * Fail closed here rather than gating each group, so a group added later cannot
 * reintroduce the leak by simply forgetting a list. This mirrors the server, where
 * containment is enforced by omission from every allow-list.
 *
 * Employee is deliberately NOT here: HR genuinely is that account's workspace, so the
 * ungated HR group is correct for them.
 */
const WORKSPACE_ONLY_USER_TYPES: readonly UserType[] = ['Training'] as const

export function getVisibleNavGroups(userType: UserType): NavGroup[] {
  const containedToOwnWorkspace = WORKSPACE_ONLY_USER_TYPES.includes(userType)
  return navigationGroups
    .filter((group) =>
      containedToOwnWorkspace
        ? // Only groups that name them. "Everyone" does not include a contained account.
          group.allowedUserTypes?.includes(userType)
        : !group.allowedUserTypes || group.allowedUserTypes.includes(userType)
    )
    .map((group) => {
      if (group.items) {
        const items = group.items.filter(
          (item) => !item.allowedUserTypes || item.allowedUserTypes.includes(userType)
        )
        return { ...group, items }
      }
      return group
    })
    .filter((group) => group.standalone || (group.items != null && group.items.length > 0))
}

/** Every reachable destination for a user type, flattened (item icon falls back to its group icon). */
export function getAllNavDestinations(userType: UserType): NavDestination[] {
  const destinations: NavDestination[] = []
  for (const group of getVisibleNavGroups(userType)) {
    if (group.standalone && group.href) {
      destinations.push({
        label: group.label,
        href: group.href,
        icon: group.icon,
        groupLabel: group.label,
      })
    } else if (group.items) {
      for (const item of group.items) {
        destinations.push({
          label: item.label,
          href: item.href,
          icon: item.icon ?? group.icon,
          groupLabel: group.label,
        })
      }
    }
  }
  return destinations
}

/** Role-filtered destinations belonging to a single group (e.g. for the avatar menu). */
export function getNavGroupDestinations(groupId: string, userType: UserType): NavDestination[] {
  const group = getVisibleNavGroups(userType).find((g) => g.id === groupId)
  if (!group?.items) return []
  return group.items.map((item) => ({
    label: item.label,
    href: item.href,
    icon: item.icon ?? group.icon,
    groupLabel: group.label,
  }))
}

/**
 * Resolve a pathname to its nav destination — exact match first, then the
 * longest href prefix (so detail routes like /accounts/123 map to Accounts).
 */
export function findDestination(pathname: string, userType: UserType): NavDestination | undefined {
  const destinations = getAllNavDestinations(userType)
  const exact = destinations.find((d) => d.href === pathname)
  if (exact) return exact
  return destinations
    .filter((d) => d.href !== '/' && pathname.startsWith(d.href))
    .sort((a, b) => b.href.length - a.href.length)[0]
}
